import { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { GridColDef } from "@mui/x-data-grid";
import { OverflowTooltip, Table } from "@4uhub/lib4uhub";
import PeopleIcon from "@mui/icons-material/People";
import { ListContactsModal } from "./ListContactsModal/ListContactsModal";
import MainApiService from "../../../../../services/mainApi.service";
import { IListContacts } from "../../../../../models/list-contact";

const LIST_CONTACTS_ROUTE =
  process.env.REACT_APP_MAIN + "/api/v1/ListContact/ByListId";
const listContactsService = new MainApiService<IListContacts>(
  LIST_CONTACTS_ROUTE
);

const translationPath = "page.4uDlist.listContacts.";

export const ListContacts = memo(() => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [showModal, setShowModal] = useState(false);
  const [contactId, setContactId] = useState<string | null>(null);
  const serviceProps = useMemo(() => {
    return {
      ListId: id,
    };
  }, [id]);

  const handleOpenModal = useCallback((id: string) => {
    setContactId(id);
    setShowModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setContactId(null);
  }, []);

  const columns: GridColDef<IListContacts>[] = [
    {
      field: "name",
      headerName: t(translationPath + "name") || "Name",
    },
    {
      field: "document",
      headerName: t(translationPath + "document") || "Document",
      renderCell: ({ row: { document } }) => (
        <OverflowTooltip
          text={document}
          tooltip={document}
          textTransform="none"
          variant="body2"
        />
      ),
    },
    {
      field: "mail",
      headerName: t(translationPath + "mail") || "Email",
      renderCell: ({ row: { mail } }) => (
        <OverflowTooltip
          text={String(mail)}
          tooltip={String(mail)}
          textTransform="none"
          variant="body2"
        />
      ),
    },
    {
      field: "whatsAppNumber",
      headerName: t(translationPath + "whatsapp_number") || "WhatsApp Number",
      renderCell: ({ row: { whatsAppNumber } }) => (
        <OverflowTooltip
          text={whatsAppNumber}
          tooltip={whatsAppNumber}
          textTransform="none"
          variant="body2"
        />
      ),
    },
    {
      field: "phone",
      headerName: t(translationPath + "phone") || "Phone",
      renderCell: ({ row: { phone } }) => (
        <OverflowTooltip
          text={phone}
          tooltip={phone}
          textTransform="none"
          variant="body2"
        />
      ),
    },
  ];

  return (
    <>
      <Table<IListContacts>
        showDefaultMacroFilters={false}
        service={listContactsService}
        serviceProps={serviceProps}
        columns={columns}
        title={t(translationPath + "title")}
        showButton={false}
        showDeleteButton={false}
        backButton
        searchInput
        rowActions={(params) => {
          if (params.row.id) {
            return [
              {
                action: () => handleOpenModal(params.row.id),
                icon: <PeopleIcon fontSize="small" />,
                text: t(translationPath + "contact_detail"),
              },
            ];
          }
          return [];
        }}
        searchInputPlaceHolder={
          t(translationPath + "search") || "Search for a contact"
        }
        confirmOptions={{
          title: t(translationPath + "confirm.title"),
          description: t(translationPath + "confirm.description"),
          confirmationButtonProps: {
            variant: "contained",
          },
        }}
      />

      <ListContactsModal
        showModal={showModal}
        onClose={handleCloseModal}
        contactId={contactId}
      />
    </>
  );
});
